/**
 * iPad Safari often delivers a touch without a click. The shell replays one
 * click from a stationary touchend. Leaflet calls stopPropagation on
 * touchstart inside `.leaflet-popup` and `.leaflet-control`, so the replay
 * has to listen in the capture phase or the finger-down point is never stored
 * and popup buttons stay dead. A transformed popup can also be missed by hit
 * testing; the finger's coordinates still pick the control.
 */

/** Chrome controls whose tap iPad Safari may deliver without a click. */
export const SHELL_TOUCH_CONTROL = [
  'aside.sidebar button.nav-link',
  'aside.sidebar a.nav-link',
  'aside.sidebar button.sidebar-toggle',
  'aside.mobile-nav-drawer button.nav-link',
  'aside.mobile-nav-drawer a.nav-link',
  'aside.mobile-nav-drawer button.mobile-nav-close',
  'button.mobile-menu-toggle',
  'a.logo-section',
  'a.topbar-breadcrumb__link',
  'button.topbar-action-btn',
  'button.settings-dropdown-item',
].join(',');

/**
 * Page controls inside the scrolling main. The same iPad scrollport that the
 * sidebar used to be never commits a click, so Detailní / Objekt in the work
 * report and a map object need the same replay.
 */
const PAGE_TOUCH_CONTROL = [
  'button',
  'a[href]',
  'summary',
  'label',
  '[role="button"]',
  'tr.tt-table-row--clickable',
].join(',');

/** Popup rows also use `<a>` without href and spans that only have `(click)`. */
const POPUP_TOUCH_CONTROL = [
  'button',
  'a',
  'summary',
  'label',
  '[role="button"]',
  'tr.tt-table-row--clickable',
].join(',');

const PAGE_TOUCH_ROOT = [
  'main.app-page-scroll',
  '.modal',
  '.leaflet-container',
  '.tt-filter-overlay-panel',
  '.tt-filter-multi-select__panel',
].join(',');

/** Overlays that must not fall through to the map or the page underneath. */
const TOUCH_OVERLAY = [
  '.leaflet-popup',
  '.leaflet-control',
  '.modal',
  '.tt-filter-overlay-panel',
  '.tt-filter-multi-select__panel',
].join(',');

const LEAFLET_TOUCH_TARGET = '.leaflet-interactive, .leaflet-marker-icon';

const FIELD_TOUCH_TARGET = [
  'input',
  'textarea',
  'select',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[contenteditable="plaintext-only"]',
].join(',');

/** Leaflet chrome. A tap here is not a control and must not hit the map. */
const POPUP_CHROME = [
  '.leaflet-popup',
  '.leaflet-popup-content-wrapper',
  '.leaflet-popup-content',
  '.leaflet-popup-tip-container',
  '.leaflet-popup-tip',
  '.leaflet-control',
].join(',');

const MOVE_PX = 12;
const GHOST_PX = 24;
const GHOST_MS = 700;

export function elementFromEventTarget(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Node) return target.parentElement;
  return null;
}

function isDisabled(el: HTMLElement): boolean {
  return el.matches(':disabled') || el.getAttribute('aria-disabled') === 'true';
}

function rectHas(el: Element, x: number, y: number): boolean {
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

function skipsHit(el: Element): boolean {
  const view = el.ownerDocument.defaultView;
  if (!view) return false;
  const style = view.getComputedStyle(el);
  return style.pointerEvents === 'none' || style.visibility === 'hidden' || style.display === 'none';
}

/** Smallest painted descendant under the finger. Used when WebKit hit-tests through a popup. */
function deepestAt(root: Element, x: number, y: number): Element | null {
  const nodes = root.querySelectorAll('*');
  let best: Element | null = null;
  let bestArea = Infinity;
  for (let i = 0; i < nodes.length; i++) {
    const el = nodes[i];
    if (skipsHit(el) || !rectHas(el, x, y)) continue;
    const rect = el.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (area < bestArea) {
      best = el;
      bestArea = area;
    }
  }
  return best;
}

/**
 * Trust the event target when it already lies in an overlay. Otherwise, if a
 * popup or modal covers the finger, WebKit hit-tested the map underneath.
 */
function resolveTouchHit(raw: Element, x: number, y: number): Element {
  const overlays = raw.ownerDocument.querySelectorAll(TOUCH_OVERLAY);
  let covering: Element | null = null;
  for (let i = 0; i < overlays.length; i++) {
    const overlay = overlays[i];
    if (!rectHas(overlay, x, y)) continue;
    if (overlay.contains(raw)) return raw;
    covering = overlay;
  }
  if (!covering) return raw;
  return deepestAt(covering, x, y) ?? covering;
}

export type PageTouchDecision =
  | { action: 'ignore' }
  | { action: 'focus-field' }
  | { action: 'swallow' }
  | { action: 'activate'; element: HTMLElement; positioned: boolean };

export function decidePageTouch(raw: Element, x: number, y: number): PageTouchDecision {
  const seen = resolveTouchHit(raw, x, y);
  if (!seen.closest(PAGE_TOUCH_ROOT)) return { action: 'ignore' };
  const overlay = seen.closest(TOUCH_OVERLAY);

  const field = seen.closest(FIELD_TOUCH_TARGET);
  if (field instanceof HTMLElement) {
    if (isDisabled(field)) return overlay ? { action: 'swallow' } : { action: 'ignore' };
    if (overlay) return { action: 'focus-field' };
    return { action: 'activate', element: field, positioned: false };
  }

  const control = seen.closest(overlay ? POPUP_TOUCH_CONTROL : PAGE_TOUCH_CONTROL);
  if (control instanceof HTMLElement) {
    if (isDisabled(control)) return overlay ? { action: 'swallow' } : { action: 'ignore' };
    return {
      action: 'activate',
      element: control,
      positioned: !!control.closest('.leaflet-popup, .leaflet-control'),
    };
  }

  if (overlay) {
    if (seen instanceof HTMLElement && !seen.matches(POPUP_CHROME)) {
      return {
        action: 'activate',
        element: seen,
        positioned: !!seen.closest('.leaflet-popup, .leaflet-control'),
      };
    }
    return { action: 'swallow' };
  }

  const leafletHit = seen.closest(LEAFLET_TOUCH_TARGET);
  if (leafletHit instanceof HTMLElement) {
    return { action: 'activate', element: leafletHit, positioned: true };
  }
  const map = seen.closest('.leaflet-container');
  if (map instanceof HTMLElement) {
    return { action: 'activate', element: map, positioned: true };
  }
  return { action: 'ignore' };
}

/**
 * Capture-phase touch replay for the shell, the page, and map popups.
 * `suppressed()` is the short window in which a sidebar category toggle
 * ignores the browser click that follows the replay.
 */
export class PageTouchReplay {
  private start: { x: number; y: number } | null = null;
  private replaying = false;
  private shellBlockUntil = 0;
  private ghost: { x: number; y: number; until: number } | null = null;
  private detach: (() => void) | null = null;

  suppressed(): boolean {
    return !this.replaying && Date.now() < this.shellBlockUntil;
  }

  /** Listen on the document before Leaflet can stop the touch. */
  attach(doc: Document): () => void {
    this.detach?.();
    const onStart = (event: Event): void => this.onTouchStart(event as TouchEvent);
    const onEnd = (event: Event): void => this.onTouchEnd(event as TouchEvent);
    const onCancel = (): void => {
      this.start = null;
    };
    const onClick = (event: Event): void => this.onGhostClick(event);
    doc.addEventListener('touchstart', onStart, true);
    doc.addEventListener('touchend', onEnd, true);
    doc.addEventListener('touchcancel', onCancel, true);
    doc.addEventListener('click', onClick, true);
    const detach = (): void => {
      doc.removeEventListener('touchstart', onStart, true);
      doc.removeEventListener('touchend', onEnd, true);
      doc.removeEventListener('touchcancel', onCancel, true);
      doc.removeEventListener('click', onClick, true);
      if (this.detach === detach) this.detach = null;
    };
    this.detach = detach;
    return detach;
  }

  private onTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) {
      this.start = null;
      return;
    }
    const touch = event.touches[0];
    this.start = { x: touch.clientX, y: touch.clientY };
  }

  private onTouchEnd(event: TouchEvent): void {
    const start = this.start;
    this.start = null;
    if (!start || event.changedTouches.length !== 1) return;
    const touch = event.changedTouches[0];
    if (Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > MOVE_PX) return;
    const raw = elementFromEventTarget(event.target);
    if (!raw) return;
    const shell = raw.closest(SHELL_TOUCH_CONTROL);
    if (shell instanceof HTMLElement && !isDisabled(shell)) {
      this.activate(event, shell, touch, false, true);
      return;
    }
    const decision = decidePageTouch(raw, touch.clientX, touch.clientY);
    if (decision.action === 'ignore' || decision.action === 'focus-field') return;
    if (decision.action === 'swallow') {
      event.preventDefault();
      this.ghost = { x: touch.clientX, y: touch.clientY, until: Date.now() + GHOST_MS };
      return;
    }
    this.activate(event, decision.element, touch, decision.positioned, false);
  }

  private activate(
    event: TouchEvent,
    el: HTMLElement,
    touch: Touch,
    positioned: boolean,
    shell: boolean,
  ): void {
    event.preventDefault();
    this.replaying = true;
    try {
      if (positioned) {
        el.dispatchEvent(new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: el.ownerDocument.defaultView,
          clientX: touch.clientX,
          clientY: touch.clientY,
          button: 0,
          detail: 1,
        }));
      } else {
        el.click();
      }
    } finally {
      this.replaying = false;
      if (shell) this.shellBlockUntil = Date.now() + GHOST_MS;
      else this.ghost = { x: touch.clientX, y: touch.clientY, until: Date.now() + GHOST_MS };
    }
  }

  private onGhostClick(event: Event): void {
    if (this.replaying) return;
    const target = elementFromEventTarget(event.target);
    if (!target) return;
    if (this.suppressed() && target.closest(SHELL_TOUCH_CONTROL)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const ghost = this.ghost;
    if (!ghost || Date.now() > ghost.until || !(event instanceof MouseEvent)) return;
    if (Math.hypot(event.clientX - ghost.x, event.clientY - ghost.y) > GHOST_PX) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.ghost = null;
  }
}
